import { FC, FocusEvent, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { SuccessModal } from 'src/components/elements/modals'
import { PhoneVerificationModal } from 'src/components/elements/phone-verification'
import { Button, Checkbox, InputField } from 'src/components/ui'
import { extractErrorMessage } from 'src/helpers'
import { api, useRegisterMutation } from 'src/redux/api'
import { useAppDispatch } from 'src/redux/hooks'
import { parsePhone } from 'src/utils/phone'
import { AsYouType } from 'libphonenumber-js/max'

interface IRegistrationForm {}

const RegistrationForm: FC<IRegistrationForm> = () => {
  const { t } = useTranslation()
  const [register] = useRegisterMutation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [openSuccessModal, setOpenSuccessModal] = useState(false)
  const [openPhoneModal, setOpenPhoneModal] = useState(false)

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      emailOrPhone: '',
      password: '',
      passwordConfirm: '',
      agreeTerms: false,
      phoneVerified: false,
    },
  })

  const emailOrPhone = watch('emailOrPhone')

  const onSubmit = async (data: any) => {
    try {
      const parsed = parsePhone(data.emailOrPhone)
      const isPhone = parsed !== null

      if (isPhone && !data.phoneVerified) {
        setOpenPhoneModal(true)
        return
      }

      const payload = {
        firstName: data.firstName,
        lastName: data.lastName,
        emailOrPhone: parsed?.e164 ?? data.emailOrPhone,
        password: data.password,
      }
      await register(payload).unwrap()

      if (isPhone) {
        // Phone signups are verified by SMS and logged in by the backend —
        // go straight to the cabinet instead of bouncing through /login.
        dispatch(api.util.invalidateTags(['User']))
        navigate('/')
        return
      }

      setOpenSuccessModal(true)
      reset()
    } catch (err) {
      console.error('[registration] register mutation failed', err)
      const message = extractErrorMessage(err, t('common.errors.unknown'))
      setError('root', { message })
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 self-stretch w-full">
        {errors.root?.message && (
          <p className="m-0 px-3.5 py-2.5 rounded-xl bg-danger-soft text-sm font-bold text-danger">
            {errors.root?.message}
          </p>
        )}
        <div className="grid md:grid-cols-2 gap-4 gap-x-3">
          <Controller
            name="firstName"
            control={control}
            rules={{ required: t('registration_form.validation.required') }}
            render={({ field }) => (
              <InputField
                {...field}
                label={t('registration_form.firstNamePlaceholder')}
                error={errors.firstName}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            rules={{ required: t('registration_form.validation.required') }}
            render={({ field }) => (
              <InputField
                {...field}
                label={t('registration_form.lastNamePlaceholder')}
                error={errors.lastName}
              />
            )}
          />
          <div className="flex flex-col">
            <Controller
              name="emailOrPhone"
              control={control}
              rules={{
                required: t('registration_form.validation.required'),
                validate: (value) => {
                  const emailPattern = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/
                  const stripped = value.replace(/\s/g, '')
                  if (!emailPattern.test(stripped) && parsePhone(stripped) === null) {
                    return t('registration_form.validation.emailOrPhone')
                  }
                  return true
                },
              }}
              render={({ field }) => (
                <InputField
                  {...field}
                  onBlur={(e: FocusEvent<HTMLInputElement>) => {
                    const value = e.target.value
                    const parsed = parsePhone(value)
                    if (parsed) {
                      const formatter = new AsYouType(parsed.country as 'UA')
                      const formatted = formatter.input(parsed.e164) || parsed.e164
                      field.onChange(formatted)
                    }
                    field.onBlur()
                  }}
                  label={t('registration_form.emailOrPhonePlaceholder')}
                  autoComplete="username"
                  error={errors.emailOrPhone}
                />
              )}
            />
            {(() => {
              const parsed = parsePhone(emailOrPhone)
              if (parsed && parsed.isMobile) {
                return <span className="text-xs text-ink-2 mt-1.5">→ {parsed.e164}</span>
              }
              if (parsed && !parsed.isMobile) {
                return (
                  <span className="text-xs font-bold text-warning mt-1.5">
                    → {parsed.e164} {t('login.phone_no_sms_hint')}
                  </span>
                )
              }
              return null
            })()}
          </div>
          <Controller
            name="password"
            control={control}
            rules={{
              required: t('registration_form.validation.required'),
              minLength: { value: 6, message: t('registration_form.validation.passwordMinLength') },
            }}
            render={({ field }) => (
              <InputField
                {...field}
                label={t('registration_form.passwordPlaceholder')}
                type="password"
                autoComplete="new-password"
                error={errors.password}
              />
            )}
          />
          <Controller
            name="passwordConfirm"
            control={control}
            rules={{
              required: t('registration_form.validation.required'),
              validate: (value) => {
                const password = watch('password')
                return value === password || t('registration_form.validation.passwordsDoNotMatch')
              },
            }}
            render={({ field }) => (
              <InputField
                {...field}
                label={t('registration_form.passwordConfirmPlaceholder')}
                type="password"
                autoComplete="new-password"
                error={errors.passwordConfirm}
              />
            )}
          />
        </div>

        <Controller
          name="agreeTerms"
          control={control}
          rules={{
            required: t('registration_form.validation.agreeTerms'),
            validate: (value) => value === true || t('registration_form.validation.agreeTerms'),
          }}
          render={({ field }) => (
            // @ts-ignore
            <Checkbox
              {...field}
              label={
                <span>
                  {t('registration_form.agreeTermsText')}{' '}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-brand-ink hover:underline"
                  >
                    {t('registration_form.privacyPolicyLink')}
                  </a>
                </span>
              }
              error={errors.agreeTerms?.message}
            />
          )}
        />

        <Button size="lg" className="w-full" type="submit">
          {t('registration_form.submitButton')}
        </Button>
      </form>
      <SuccessModal
        title={t('registration_form.successTitle')}
        text={t('registration_form.successText')}
        buttonText={t('registration_form.successButton')}
        open={openSuccessModal}
        handleClose={() => setOpenSuccessModal(false)}
      />

      <PhoneVerificationModal
        open={openPhoneModal}
        handleClose={() => setOpenPhoneModal(false)}
        phoneNumber={parsePhone(emailOrPhone)?.e164 ?? emailOrPhone}
        onVerificationSuccess={() => {
          setValue('phoneVerified', true)
          handleSubmit(onSubmit)()
          setOpenPhoneModal(false)
        }}
      />
    </>
  )
}

export default RegistrationForm
